import type { CategoryCode } from '@typings/Category';
import type {
	ParsedValues,
	RecentlySearchedDirectory,
	RecentlySearchedDirectoryKeys,
	UnparsedValues,
} from '@typings/recentlySearchedDirectories';
import type { AppliedFilterPillProps } from '@typings/search';
import { parseISO } from 'date-fns';

interface OrderRecentlySeenDirectoriesParams {
	marketCode: string | null;
	category: CategoryCode;
	city: string | null;
	stateCode: string | null;
	appliedFilterPills?: AppliedFilterPillProps[];
	semanticSearch?: string | null;
}

interface FindDirectoryByEqualityParams {
	directories: RecentlySearchedDirectory[];
	marketCode: string | null;
	category: CategoryCode;
	city: string | null;
	stateCode: string | null;
	appliedFilterPills?: AppliedFilterPillProps[];
	semanticSearch?: string | null;
}

const RECENTLY_SEARCHED_DIRECTORIES_KEY =
	'app:TKMarketplace:RecentlySearchedDirectories';

const CURRENT_VERSION = 1;

const jsonParseReviver = (
	key: RecentlySearchedDirectoryKeys,
	value: UnparsedValues,
): ParsedValues => {
	if (key === 'dateViewed' && typeof value === 'string') {
		return new Date(value);
	}
	return value;
};

const getDirectoriesFromLocalStorage = ():
	| null
	| RecentlySearchedDirectory[] => {
	const result = window.localStorage.getItem(RECENTLY_SEARCHED_DIRECTORIES_KEY);

	if (result) {
		try {
			const parsed: RecentlySearchedDirectory[] = JSON.parse(
				result,
				jsonParseReviver,
			);

			// Check if we need to migrate - if no version or version < 1, clear data
			if (!parsed.length || !parsed[0].version || parsed[0].version < 1) {
				window.localStorage.removeItem(RECENTLY_SEARCHED_DIRECTORIES_KEY);
				return null;
			}

			return parsed;
		} catch {
			// If parsing fails, clear data and start fresh
			window.localStorage.removeItem(RECENTLY_SEARCHED_DIRECTORIES_KEY);
			return null;
		}
	}

	return null;
};

const setDirectoriesToLocalStorage = (
	directories: RecentlySearchedDirectory[],
) => {
	const stringifiedData = JSON.stringify(directories);
	window.localStorage.setItem(
		RECENTLY_SEARCHED_DIRECTORIES_KEY,
		stringifiedData,
	);
};

const sortDirectories = (
	directories: RecentlySearchedDirectory[],
): RecentlySearchedDirectory[] =>
	directories.sort((a, b) => b.dateViewed.getTime() - a.dateViewed.getTime());

const appliedFilterPillsAreEqual = (
	a: AppliedFilterPillProps[] = [],
	b: AppliedFilterPillProps[] = [],
): boolean => {
	if (a.length !== b.length) return false;

	// Sort both arrays by value for consistent comparison
	const sortedA = [...a].sort((x, y) => x.value.localeCompare(y.value));
	const sortedB = [...b].sort((x, y) => x.value.localeCompare(y.value));

	// Compare each filter individually to avoid issues with object property order
	return sortedA.every((filterA, index) => {
		const filterB = sortedB[index];
		// Compare all relevant properties that matter for equality
		return (
			filterA.value === filterB.value &&
			filterA.id === filterB.id &&
			filterA.name === filterB.name &&
			filterA.categorySlug === filterB.categorySlug
		);
	});
};

const semanticSearchAreEqual = (
	a?: string | null,
	b?: string | null,
): boolean => {
	// Normalize undefined to null for consistent comparison
	const normalizeSemanticSearch = (search?: string | null): string | null =>
		search ?? null;

	const normalizedA = normalizeSemanticSearch(a);
	const normalizedB = normalizeSemanticSearch(b);

	// Simple strict comparison
	return normalizedA === normalizedB;
};

const directoriesAreEqual = (
	existing: RecentlySearchedDirectory,
	target: {
		marketCode: string | null;
		category: CategoryCode;
		city?: string | null;
		stateCode?: string | null;
		semanticSearch?: string | null;
	},
): boolean => {
	return (
		existing.marketCode === target.marketCode &&
		existing.category === target.category &&
		existing.city === target.city &&
		existing.stateCode === target.stateCode &&
		semanticSearchAreEqual(existing.semanticSearch, target.semanticSearch)
	);
};

const findDirectoryByEquality = ({
	directories,
	marketCode,
	category,
	city = null,
	stateCode = null,
	appliedFilterPills = [],
	semanticSearch = null,
}: FindDirectoryByEqualityParams): RecentlySearchedDirectory | undefined => {
	return directories.find((d) =>
		directoriesAreEqual(d, {
			marketCode,
			category,
			city,
			stateCode,
			semanticSearch,
		}),
	);
};

const updateDirectoryData = (
	directories: RecentlySearchedDirectory[],
	marketCode: string | null,
	category: CategoryCode,
	city: string | null = null,
	stateCode: string | null = null,
	appliedFilterPills: AppliedFilterPillProps[] = [],
	semanticSearch: string | null = null,
): RecentlySearchedDirectory[] => {
	const foundDirectory = findDirectoryByEquality({
		directories,
		marketCode,
		category,
		city,
		stateCode,
		appliedFilterPills,
		semanticSearch,
	});

	if (foundDirectory) {
		const filteredDirectories = directories.filter(
			(d) =>
				!directoriesAreEqual(d, {
					marketCode,
					category,
					city,
					stateCode,
					semanticSearch,
				}),
		);

		const updatedDirectoryData: RecentlySearchedDirectory = {
			...foundDirectory,
			appliedFilterPills, // Update with new filters
			dateViewed: parseISO(new Date().toISOString()),
		};

		filteredDirectories.unshift(updatedDirectoryData);
		return filteredDirectories;
	}

	const newlySeenDirectory: RecentlySearchedDirectory = {
		marketCode,
		category,
		city,
		stateCode,
		appliedFilterPills,
		semanticSearch,
		dateViewed: parseISO(new Date().toISOString()),
		version: CURRENT_VERSION,
	};

	directories.unshift(newlySeenDirectory);
	return directories.slice(0, 50);
};

const orderRecentlySeenDirectories = ({
	marketCode,
	category,
	city = null,
	stateCode = null,
	appliedFilterPills = [],
	semanticSearch = null,
}: OrderRecentlySeenDirectoriesParams) => {
	const localStorageDirectoryData = getDirectoriesFromLocalStorage();
	if (localStorageDirectoryData) {
		const sortedDirectories = sortDirectories(localStorageDirectoryData);
		const updatedDirectories = updateDirectoryData(
			sortedDirectories,
			marketCode,
			category,
			city,
			stateCode,
			appliedFilterPills,
			semanticSearch,
		);
		setDirectoriesToLocalStorage(updatedDirectories);
		return;
	}

	const initialDirectory: RecentlySearchedDirectory[] = [
		{
			marketCode,
			dateViewed: parseISO(new Date().toISOString()),
			category,
			city,
			stateCode,
			appliedFilterPills,
			semanticSearch,
			version: CURRENT_VERSION,
		},
	];

	setDirectoriesToLocalStorage(initialDirectory);
};

export { orderRecentlySeenDirectories };
